import { useEffect, useState } from 'react';
import { FileText, X } from 'lucide-react';
import type { InboxAttachment } from '../../types/inbox';
import { attachmentKind } from '../../lib/inboxView';
import { upgradeGooglePhotoSize } from '../../lib/googlePhotoUrl';
import { cn } from '../../lib/utils';

// Community Inbox Part E1 — inline attachment previews + a lightbox. Renders
// image attachments as thumbnails (click → full-size overlay), videos inline
// (playable), and anything else as a labelled link. Used by the comment feed
// (MessageRow) and DM bubbles for BOTH inbound (customer) and outbound (our
// reply) attachments — no more bare "see attachment" links.

function Lightbox({ url, onClose }: { url: string; onClose: () => void }) {
  // Open a best-effort full-size variant (Google review photos arrive as
  // thumbnails), falling back to the original if that URL doesn't load — so an
  // unhonoured size hint degrades to the thumbnail, never a broken image.
  const [src, setSrc] = useState(() => upgradeGooglePhotoSize(url) ?? url);
  useEffect(() => {
    setSrc(upgradeGooglePhotoSize(url) ?? url);
  }, [url]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Attachment preview"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-6"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={src}
        alt="Attachment"
        onError={() => {
          if (src !== url) setSrc(url);
        }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl"
      />
    </div>
  );
}

export function MessageAttachments({
  attachments,
  className,
  tone = 'light',
}: {
  attachments: InboxAttachment[];
  className?: string;
  // Link colour for "other" attachments, matching the bubble background.
  tone?: 'light' | 'dark';
}) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  // Google-hosted photo urls can expire or rate-limit. A thumbnail that fails to
  // load is dropped entirely rather than left as a broken-image frame — the same
  // honest-empty-state posture as the IG post media.
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const withUrl = attachments.filter((a) => Boolean(a.url) && !failed.has(a.url!));
  if (withUrl.length === 0) return null;

  return (
    <div className={cn('mt-1.5 flex flex-wrap gap-2', className)}>
      {withUrl.map((att, i) => {
        const url = att.url!;
        const kind = attachmentKind(att);
        if (kind === 'image') {
          return (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(url)}
              title="View full size"
              className="block overflow-hidden rounded-md border border-black/5 hover:opacity-90"
            >
              <img
                src={url}
                alt="Attachment"
                loading="lazy"
                onError={() => setFailed((prev) => new Set(prev).add(url))}
                className="h-32 w-32 object-cover"
              />
            </button>
          );
        }
        if (kind === 'video') {
          return (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              key={i}
              src={url}
              controls
              muted
              playsInline
              preload="metadata"
              className="max-h-48 max-w-full rounded-md bg-black"
            />
          );
        }
        return (
          <a
            key={i}
            // Prefer the original source link for the click-through (a GIF's
            // l.facebook.com redirect); `url` is the rendered preview.
            href={att.sourceUrl || url}
            target="_blank"
            rel="noreferrer"
            className={cn(
              'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs',
              tone === 'dark'
                ? 'border-white/20 text-kyck-50 hover:bg-white/10'
                : 'border-slate-200 text-kyck-700 hover:bg-slate-50',
            )}
          >
            <FileText className="h-3.5 w-3.5" />
            {att.type && att.type !== 'unknown' ? `${att.type} attachment` : 'Attachment'}
          </a>
        );
      })}
      {lightbox && <Lightbox url={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
